import { GripVertical, Trash2 } from '@tamagui/lucide-icons';
import { Pressable } from '@unistocks/components/basic/pressable';
import {
  DraggableItemItemContainer,
  useDraggableItemStyle
} from '@unistocks/components/sortables/draggable-item';
import { Paragraph } from '@unistocks/components/text/text';
import { DashboardItemSettingsDto } from '@unistocks/proxy/axios-client';
import {
  FadeInLeftTransition,
  FadeOutLeftTransition,
  QuickerTransition
} from '@unistocks/shared/animations';
import { SUBTITLE_OPACITY } from '@unistocks/shared/consts';
import { t } from '@unistocks/shared/localization/index.ts';
import { FieldArrayWithId } from 'react-hook-form';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';
import { useItemContext } from 'react-native-sortables';
import { getVariableValue, ViewProps } from 'tamagui';

export type CalculatedValueArrayItem = FieldArrayWithId<DashboardItemSettingsDto, 'values', 'id'>;

export type EventDraggableItemProps = {
  draggable?: boolean;
  item: CalculatedValueArrayItem;
  onPress: ViewProps['onPress'];
  onDeletePress: () => void;
};

export const EventDraggableItem: React.FC<EventDraggableItemProps> = ({
  draggable = true,
  item,
  onPress,
  onDeletePress
}) => {
  const { displayName, valueType, valueSource, nestedValues } = item;
  const typeString = t(`::Enum:ValueType:${valueType}`, {
    defaultValue: t('::Dashboard:Settings:ValueType:Combination')
  });
  const sourceString = t(
    `::Enum:ValueSource:${valueSource || (nestedValues && nestedValues[0].valueSource)}`
  );

  const margin3 = getVariableValue('$3', 'space');
  const { isActive } = useItemContext();
  const draggableItemStyle = useDraggableItemStyle();

  const handleStyle = useAnimatedStyle(() => {
    return {
      display: isActive.value ? 'none' : 'flex'
    };
  });

  return (
    <DraggableItemItemContainer
      px={'$2'}
      py={'$2.5'}
      justifyContent={'space-between'}
      cursor={'pointer'}
      hoverStyle={{ backgroundColor: '$backgroundHover' }}
      pressStyle={{ backgroundColor: '$backgroundPress' }}
      style={draggableItemStyle}
      onPress={onPress}
    >
      {draggable ? (
        <Animated.View
          entering={FadeInLeftTransition}
          exiting={FadeOutLeftTransition}
          style={handleStyle}
        >
          <GripVertical color={'$unistocks'} cursor="grab" />
        </Animated.View>
      ) : null}

      <Animated.View layout={QuickerTransition} style={{ marginLeft: margin3, flex: 1 }}>
        <Paragraph size={'$5'} userSelect="none">
          {displayName}
        </Paragraph>
        <Paragraph size={'$3'} o={SUBTITLE_OPACITY} userSelect="none">
          {`${typeString}, ${sourceString}`}
        </Paragraph>
      </Animated.View>

      <Pressable
        withFeedback
        withScale
        onPress={e => {
          e.stopPropagation();
          onDeletePress();
        }}
      >
        <Trash2 color={'$danger'} />
      </Pressable>
    </DraggableItemItemContainer>
  );
};
